import { ElementTemplate } from '../types/types';

/**
 * Maps defaultSettings from API element templates to canvas element initial properties
 * Each element type has its own structure in the API response
 */
export const getElementDefaultSettings = (
  elementType: string,
  templates: ElementTemplate[] | undefined
): Record<string, any> => {  
  if (!templates) return {};

  const template = templates.find(
    t => t.accessKey === elementType || t.accessKey === elementType.replace('_', '-')
  );
  if (!template) return {};

  const { styleSettings, html } = template.defaultSettings;  

  // Map element-specific settings based on type
  switch (elementType) {
    case 'heading':
      return {
        htmlContent: typeof html?.content === 'string' ? html.content : 'Heading Goes Here',
        content: typeof html?.content === 'string' ? html.content : 'Heading Goes Here',
        heading: styleSettings?.headingLevel || 'h1',
        fontSize: styleSettings?.fontSize || '48px',
        fontWeight: styleSettings?.fontWeight || 'bold',
        textAlignment: styleSettings?.textAlign || 'left',
        color: styleSettings?.color || '#1a3a5c'
      };

    case 'paragraph':
      return {
        htmlContent: typeof html?.content === 'string' ? html.content : 'This is a paragraph. Add your content here.',
        paragraphContent: typeof html?.content === 'string' ? html.content : 'This is a paragraph. Add your content here.',
        paragraphFontSize: styleSettings?.fontSize|| '14px',
        paragraphFontWeight: styleSettings?.fontWeight || 'regular',
        paragraphTextAlignment: styleSettings?.textAlign || 'left',
        paragraphColor: styleSettings?.color || '#495057'
      };

    case 'separator':
      return {
        separatorHeight: styleSettings?.appearance?.height || '2px',
        separatorColor: styleSettings?.appearance?.color || '#BABDC1',
        separatorType: styleSettings?.appearance?.type || 'solid',
        alignment: styleSettings?.alignment || 'left'
      };

    case 'button':
      return {
        htmlContent: typeof html?.content === 'string' ? html.content : 'Click Here',
        content: typeof html?.content === 'string' ? html.content : 'Click Here',
        buttonColor: styleSettings?.styling?.buttonColor || '#0050ff',
        buttonTextColor: styleSettings?.styling?.textColor || '#D0E8FF',
        buttonHoverColor: styleSettings?.styling?.hoverColor || '#003FC7',
        buttonAlignment: styleSettings?.alignment || 'left',
        buttonLink: styleSettings?.interactivity?.url || '',
        openNewTab: styleSettings?.interactivity?.openInNewTab || false
      };

    case 'media':
    case 'image':
    case 'video':
      return {
        htmlContent: typeof html?.content === 'string' ? html.content : '',
        mediaType: styleSettings?.mediaType || 'image',
        alignment: styleSettings?.alignment || 'left',
        textAlignment: styleSettings?.alignment || 'center',
        linkURL: styleSettings?.interactivity?.link || '',
        openNewTab: styleSettings?.interactivity?.openInNewTab || false,
        content: typeof html?.content === 'string' ? html.content : ''
      };

    case 'form':
      return {
        formScopeId: styleSettings?.formScopeId || null,
        formId: styleSettings?.formId || null
      };

    case 'search_query':
      return {
        searchQueryScopeId: styleSettings?.searchQueryScopeId || null,
        searchQueryId: styleSettings?.searchQueryId || null
      };

    case 'header':
      return {
        htmlContent: html?.content || '',
        menuId: styleSettings?.content?.menuId || null,
        textColor: styleSettings?.styling?.textColor || '#0050ff',
        backgroundColor: styleSettings?.styling?.backgroundColor || '#D0E8FF',
        headerAlignment: styleSettings?.layout?.type || 'left-right'
      };

    case 'footer':
      return {
        htmlContent: html?.content || '',
        overview: html?.content?.overview || styleSettings?.overview || '',
        menuId: html?.content?.menuId || null,
        showPrivacyPolicy: html?.content?.showPrivacyPolicy || false,
        showTermsAndConditions: html?.content?.showTermsAndConditions || false,
        textColor: styleSettings?.textColor || '#0050ff',
        backgroundColor: styleSettings?.backgroundColor || '#0D6BFF',
        socialMedia: styleSettings?.socialMedia || {
          facebook: { enabled: false, url: '' },
          linkedin: { enabled: false, url: '' },
          youtube: { enabled: false, url: '' },
          twitter: { enabled: false, url: '' }
        }
      };

    case 'single_column':
    case 'multi_column':
      return {
        columns: styleSettings?.columns || (elementType === 'multi_column' ? 2 : 1)
      };

    default:
      return {};
  }
};

/**
 * Extracts content from API defaultSettings
 * Handles both simple string content and nested HTML objects
 */
export const getDefaultContent = (elementType: string, templates: ElementTemplate[] | undefined): string => {
  if (!templates) return '';

  const template = templates.find(
    t => t.accessKey === elementType || t.accessKey === elementType.replace('_', '-')
  );

  if (!template) return '';

  const content = template.defaultSettings.html?.content;
  
  // Handle different content types
  if (typeof content === 'string') return content;
  if (typeof content === 'object' && content !== null) {
    // For complex objects like footer content, return empty string
    // These will be handled by their specific property components
    return '';
  }
  return '';
};

/**
 * Extracts the full HTML configuration from defaultSettings
 * Returns both tag and content information
 */
export const getDefaultHtmlConfig = (elementType: string, templates: ElementTemplate[] | undefined): any => {
  if (!templates) return {};

  const template = templates.find(
    t => t.accessKey === elementType || t.accessKey === elementType.replace('_', '-')
  );

  if (!template) return {};

  const htmlConfig = template.defaultSettings.html || {};
  return {
    tag: htmlConfig.tag || 'div',
    content: htmlConfig.content || '',
    attributes: htmlConfig.attributes || {}
  };
};

/**
 * Creates a complete element with defaultSettings from API
 */
export const createElementWithDefaults = (
  elementType: string,
  templates: ElementTemplate[] | undefined
): { content: string; properties: Record<string, any> } => {
  const content = getDefaultContent(elementType, templates);
  const properties = getElementDefaultSettings(elementType, templates);
  const htmlConfig = getDefaultHtmlConfig(elementType, templates);

  return {
    content,
    properties: {
      ...properties,
      htmlConfig // Include full HTML configuration for rendering
    }
  };
};

/**
 * Returns default section properties based on section type
 * Includes only properties that are editable in SectionProperties UI
 * Applied automatically when sections are created or transformed
 */
export const getSectionDefaultProperties = (sectionType: 'single_column' | 'multi_column'): Record<string, any> => {
  const baseDefaults = {
    // Container Background
    bgColor: '#0556CB0D',                    // Background color
    
    // Background Media
    mediaType: 'Image',                        // Image or Video
    imageUrl: '',                              // Image file key/URL
    imagePreview: '',                          // Image preview data
    imageExternalURL: '',                      // External image URL
    imageFileName: '',                         // Image file name
    imageFileSize: undefined,                  // Image file size
    videoUrl: '',                              // Video file key/URL
    videoPreview: '',                          // Video preview data
    videoExternalURL: '',                      // External video URL
    videoFileName: '',                         // Video file name
    videoFileSize: undefined,                  // Video file size
    
    // Image Overlay
    imageOverlayColor: '#000000',              // Overlay color
    imageOverlayOpacity: '0%'                  // Overlay opacity
  };

  return baseDefaults;
};
